/*
 * Fișa unui tichet deschis direct din formularul „Cerere".
 *
 * ---------------------------------------------------------------------------
 * DE CE ARE ECRAN PROPRIU, ȘI NU FIȘA UNEI CERERI
 * ---------------------------------------------------------------------------
 *
 * Un tichet direct nu e o cerere de magazin: n-are articole, aprobări sau
 * jurnal. Fișa cererii ar fi arătat trei secțiuni goale și una plină, iar omul
 * ar fi rămas cu impresia că i s-a pierdut ceva pe drum.
 *
 * Și id-urile celor două nu vin din același șir — `/requests/12` și
 * `/tickets/12` sunt lucruri diferite.
 *
 * ---------------------------------------------------------------------------
 * COMENTARIILE SUNT DOAR CELE PUBLICE
 * ---------------------------------------------------------------------------
 *
 * Api-ul trimite numai `visibility = 1`; notele interne ale operatorilor nu ies
 * de acolo. Ce ajunge aici se poate arăta fără grijă — dar tăierea se face în
 * api, nu în pagină: o filtrare făcută pe ecran ar fi însemnat că datele au
 * ajuns deja în browser.
 */

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import * as shop from '../api/shop';
import { ApiError } from '../api/errors';
import { Badge } from '../components/Badge';
import { FilePreview } from '../components/FilePreview';
import type { TicketAttachment } from '../api/types';
import { ErrorState, Loading } from '../components/states';
import { useToasts } from '../components/ToastContext';
import { formatBytes, formatDateTime } from '../lib/format';
import { hasNewReply, markReplySeen } from '../lib/replies';
import { Icon } from '../components/Icon';
import { t } from '../lib/i18n';
import { useAsync } from '../lib/useAsync';

const EM_DASH = '—';

export function TicketDetailScreen() {
  const { id = '' } = useParams();
  const found = useAsync((signal) => shop.directTicket(id, { signal }), [id]);
  const { push } = useToasts();

  /* Ce fisier e deschis in fereastra de previzualizare. `null` = niciunul. */
  const [preview, setPreview] = useState<TicketAttachment | null>(null);

  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [replyError, setReplyError] = useState<string | null>(null);
  const [files, setFiles] = useState<File[]>([]);

  const lastStaffId = found.data?.ticket?.last_staff_comment_id ?? 0;

  /*
   * Ce era nou devine vazut, dar NUMAI dupa ce s-a desenat.
   *
   * Se retine intr-o stare a ecranului daca erau raspunsuri nevazute la
   * DESCHIDERE - fiindca randul de mai jos le sterge semnul imediat. Fara
   * poza asta, clopotelul de pe fisa nu s-ar fi aprins niciodata: se stingea
   * in aceeasi clipa in care ar fi trebuit sa apara.
   */
  const [hadNew] = useState(() => hasNewReply(id, lastStaffId));

  useEffect(() => {
    if (lastStaffId > 0) markReplySeen(id, lastStaffId);
  }, [id, lastStaffId]);

  const send = async (event: React.FormEvent) => {
    event.preventDefault();

    const text = reply.trim();
    if (text === '' || sending) return;

    setSending(true);
    setReplyError(null);

    try {
      await shop.addTicketComment(id, text);

      /*
       * Se goleste caseta si se RECITESTE fisa, nu se lipeste comentariul in
       * lista din memorie. Lipit local, ecranul ar fi aratat drept scris ceva
       * ce inca nu se stie ca a ajuns in baza; iar recitirea aduce si ce a
       * scris intre timp operatorul.
       */
      /*
       * Fisierele pleaca DUPA text, si separat.
       *
       * Doua cereri, nu una: un fisier respins - prea mare, tip nepermis - n-are
       * voie sa duca la pierderea a ceea ce omul a scris. Textul e deja la
       * adapost cand incepe incarcarea, iar daca fisierele pica, mesajul spune
       * exact asta, si textul nu se mai cere o data.
       */
      if (files.length > 0) {
        try {
          await shop.addTicketFiles(id, files);
        } catch (cause) {
          setFiles([]);
          found.reload();

          const fields = cause instanceof ApiError ? cause.fields : undefined;

          setReplyError(
            fields?.files
              ?? (cause instanceof Error ? cause.message : t('The files could not be sent.')),
          );

          return;
        }
      }

      setReply('');
      setFiles([]);
      found.reload();
      push(t('Your reply has been sent.'));
    } catch (cause) {
      /*
       * Eroarea sta LANGA caseta, nu intr-un dreptunghi care dispare in cinci
       * secunde: aici omul mai are ce face - sa scurteze textul, sa incerce din
       * nou - si textul lui e inca in caseta, deci nu s-a pierdut nimic.
       */
      const fields = cause instanceof ApiError ? cause.fields : undefined;

      setReplyError(
        fields?.comment
          ?? (cause instanceof Error ? cause.message : t('The reply could not be sent.')),
      );
    } finally {
      setSending(false);
    }
  };

  if (found.status === 'loading') return <Loading label={t('Loading ticket…')} />;


  if (found.status === 'error' && found.error) {
    return <ErrorState error={found.error} onRetry={found.reload} />;
  }

  const ticket = found.data?.ticket;

  if (!ticket) return null;

  return (
    <>
      <header className="page-head">
        <div>
          <p className="micro-label">{t('Ticket')} #{ticket.id}</p>
          <h1 className="page-title">
            {ticket.subject}
            {/*
              * Clopotelul sta LANGA TITLU, nu deasupra coloanei de revenire:
              * cand omul deschide fisa, primul lucru pe care il citeste e
              * titlul, si acolo trebuie sa afle ca s-a intamplat ceva. Dispare
              * la urmatoarea deschidere - ce s-a citit ramane citit.
              */}
            {hadNew ? (
              <span className="title-bell" title={t('The service desk has replied')}>
                <Icon name="bell" />
                <span className="sr-only">{t('The service desk has replied')}</span>
              </span>
            ) : null}
          </h1>
        </div>
        <Link className="btn" to="/requests">{t('Back to my requests')}</Link>
      </header>

      {/*
        * Doua coloane: la stanga CE e tichetul, la dreapta CE facem cu el.
        *
        * Fisa si descrierea se citesc o data si raman acolo; revenirea se
        * foloseste. Una sub alta, caseta de scris ajungea sub linia ecranului
        * la orice tichet cu descriere mai lunga - trebuia derulat pana jos ca
        * sa se vada ca exista. Alaturi, se vede din prima ca ai unde raspunde.
        *
        * Sub o mie de pixeli coloanele se aseaza una sub alta, in aceeasi
        * ordine: intai ce e, apoi ce faci.
        */}
      <div className="split split--thirds">
        <div className="split__main">
      {/*
        * Descrierea PRIMA, sub titlu.
        *
        * Titlul e o propozitie scurta - "Calculator alb, eu am negru" - iar
        * dedesubt urma un tabel de stari si date. Omul care deschide fisa vrea
        * intai sa vada CE a cerut, cu propriile lui cuvinte; starea si cine se
        * ocupa sunt raspunsul la a doua intrebare, nu la prima.
        */}
      {ticket.description !== '' ? (
        <section className="panel">
          <h2 className="panel__title">{t('Description')}</h2>
          {/* Text, nu HTML: descrierea vine de la om, scrisă în formularul de pe
              ecranul „Cererea ta". `white-space: pre-wrap` îi ține rândurile. */}
          <p className="ticket__description">{ticket.description}</p>
        </section>
      ) : null}

      {ticket.attachments && ticket.attachments.length > 0 ? (
        <section className="panel">
          <h2 className="panel__title">{t('Files')}</h2>
          {/* Si ale lui, si ale service desk-ului: daca operatorul trimite un
              formular de completat, tichetul e locul in care omul il cauta. */}
          <ul className="files">
            {ticket.attachments.map((file) => (
              <li key={file.id}>
                {/*
                  * Randul e un BUTON, nu un link: nu duce nicaieri, deschide
                  * ceva peste pagina. Un `<a href="#">` ar fi promis o adresa si
                  * ar fi pus o diez in bara de adrese la fiecare apasare.
                  */}
                <button
                  type="button"
                  className="files__item files__item--open"
                  onClick={() => setPreview(file)}
                >
                  <Icon name={file.is_image ? 'image' : 'clip'} />
                  <span className="files__name">{file.name}</span>
                  <span className="table__muted">{formatBytes(file.size)}</span>
                  <span className="files__cue">{t('Preview')}</span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <section className="panel">
        <dl className="facts">
          <dt>{t('Status')}</dt>
          <dd><Badge state={ticket.status ?? undefined} /></dd>

          {ticket.type ? (
            <>
              <dt>{t('Ticket type')}</dt>
              <dd>{ticket.type}</dd>
            </>
          ) : null}

          <dt>{t('Submitted')}</dt>
          <dd>{formatDateTime(ticket.submitted_at)}</dd>

          <dt>{t('Last update')}</dt>
          <dd>{ticket.updated_at ? formatDateTime(ticket.updated_at) : EM_DASH}</dd>

          {/*
            * Cine se ocupă. Numele omului când există, altfel grupul — un tichet
            * ajuns la o echipă dar neluat încă de nimeni e o stare adevărată, nu
            * o lipsă, și e chiar răspunsul la întrebarea asta.
            */}
          <dt>{t('Assigned to')}</dt>
          <dd>{ticket.assignee ?? t('nobody yet')}</dd>

          {/*
            * Cine a cerut — adică chiar cel care se uită.
            *
            * Pare de prisos: omul îşi ştie numele. Nu e. Ăsta e primul loc în
            * care poate vedea PE CE DATE îl va căuta cineva din IT, iar un
            * telefon vechi în fişa lui de angajat înseamnă un tichet la care nu
            * răspunde nimeni când se sună. Costă un rând acum şi două zile
            * altfel.
            *
            * Secţiunea lipseşte cu totul când api-ul nu trimite `requester` —
            * un ecran mai nou decât api-ul din spate nu desenează câmpuri goale.
            */}
          {ticket.requester ? (
            <>
              <dt>{t('Requested by')}</dt>
              <dd>{ticket.requester.name ?? EM_DASH}</dd>

              {ticket.requester.email || ticket.requester.phone ? (
                <>
                  <dt>{t('Contact')}</dt>
                  <dd>
                    {ticket.requester.email ? (
                      <a href={`mailto:${ticket.requester.email}`}>{ticket.requester.email}</a>
                    ) : null}
                    {ticket.requester.email && ticket.requester.phone ? ' · ' : null}
                    {ticket.requester.phone ?? null}
                  </dd>
                </>
              ) : null}

              {/*
                * Numărul se scrie numai de la 2 în sus: „1 tichet în ultimele 7
                * zile" e chiar ăsta, şi ar fi fost o observaţie despre nimic.
                * De la două încolo e un tipar, şi tiparele se rezolvă altfel
                * decât incidentele.
                */}
              {(ticket.requester.tickets_7d ?? 0) > 1 ? (
                <>
                  <dt>{t('Lately')}</dt>
                  <dd>
                    {t('Tickets you opened in the last 7 days:')}{' '}
                    <strong>{ticket.requester.tickets_7d}</strong>
                  </dd>
                </>
              ) : null}
            </>
          ) : null}
        </dl>
      </section>


        </div>

        <aside className="split__side">
      {/*
        * Revenirea, nu „comentariile".
        *
        * Până acum ecranul arăta ce scriseseră operatorii şi atât. Omul citea
        * „avem nevoie de numărul de inventar" şi n-avea unde să-l scrie:
        * singurul drum înapoi era mailul sau telefonul, adică în afara
        * tichetului, unde răspunsul lui nu mai ajungea niciodată lângă
        * întrebare. Un tichet pe care poţi doar să-l citeşti nu e o cerere
        * urmărită, e un afiş.
        *
        * Caseta stă SUB firul discuţiei, ca la orice conversaţie: se citeşte
        * până jos şi acolo se răspunde. Deasupra, ar fi cerut un răspuns
        * înainte să se fi citit întrebarea.
        */}
      <section className="panel">
        <h2 className="panel__title">{t('Follow-up')}</h2>

        {ticket.comments.length === 0 ? (
          /* Gol nu înseamnă „nu s-a întâmplat nimic": înseamnă că nimeni nu
             ți-a scris încă. Diferența contează pentru cine așteaptă. */
          <p className="table__muted">{t('Nobody has written on this ticket yet.')}</p>
        ) : (
          <ol className="comments">
            {ticket.comments.map((comment, i) => (
              <li
                /*
                 * Rândurile lui se desenează altfel decât ale service desk-ului.
                 * Într-o conversaţie trebuie să se vadă din prima cine a spus
                 * ce; un fir în care cele două laturi arată la fel se citeşte de
                 * două ori.
                 */
                className={
                  'comments__item'
                  + (comment.mine ? ' comments__item--mine' : '')
                  + (comment.wants_files ? ' comments__item--asks' : '')
                }
                key={`${comment.created ?? ''}-${i}`}
              >
                <p className="comments__who">
                  <strong>{comment.mine ? t('You') : (comment.author ?? t('System'))}</strong>
                  {comment.created ? (
                    <span className="table__muted"> · {formatDateTime(comment.created)}</span>
                  ) : null}
                </p>
                <p className="comments__body">{comment.comment}</p>

                {/*
                  * Cererea de fisiere isi poarta butonul CU EA, sub textul care
                  * a cerut. Pana acum, "trimite-mi poza erorii" ajungea ca
                  * text, iar omul nu avea unde sa puna poza: raspundea pe mail,
                  * adica in afara tichetului, unde raspunsul lui nu mai ajungea
                  * niciodata langa intrebare.
                  *
                  * Butonul apasa acelasi camp de fisiere de jos - nu deschide
                  * un al doilea drum. Asa, ce alege aici pleaca odata cu
                  * raspunsul, si se vede in aceeasi lista inainte sa trimita.
                  */}
                {comment.wants_files ? (
                  <p className="comments__ask">
                    <button
                      type="button"
                      className="btn btn--small"
                      onClick={() => document.getElementById('ticket-files')?.click()}
                    >
                      <Icon name="clip" />
                      <span>{t('Attach files')}</span>
                    </button>
                  </p>
                ) : null}
              </li>
            ))}
          </ol>
        )}

        <form className="reply" onSubmit={send}>
          <label className="field__label" htmlFor="ticket-reply">
            {t('Write to the service desk')}
          </label>
          <textarea
            id="ticket-reply"
            className="field__control"
            rows={3}
            maxLength={4000}
            aria-invalid={replyError ? true : undefined}
            placeholder={t('Add something, or answer what you were asked.')}
            value={reply}
            onChange={(event) => {
              setReply(event.target.value);
              if (replyError) setReplyError(null);
            }}
          />

          {/*
            * Fisierele stau SUB caseta, nu deasupra: se scrie intai ce e de
            * spus, iar poza vine ca dovada. Deasupra, ar fi cerut un fisier
            * inainte sa se fi formulat problema.
            */}
          <div className="reply__files">
            <label className="reply__pick" htmlFor="ticket-files">
              <Icon name="clip" />
              <span>{t('Attach files')}</span>
            </label>
            <input
              id="ticket-files"
              className="sr-only"
              type="file"
              multiple
              accept="image/*,application/pdf"
              onChange={(event) => {
                setFiles(Array.from(event.target.files ?? []));
                if (replyError) setReplyError(null);
              }}
            />

            {files.length > 0 ? (
              <ul className="reply__chosen">
                {files.map((file) => (
                  <li key={file.name}>
                    {file.name} <span className="table__muted">{formatBytes(file.size)}</span>
                  </li>
                ))}
              </ul>
            ) : (
              /* Se spune de la inceput ce se accepta. Un om care afla abia dupa
                 ce a ales un fisier de 40 MB ca nu se poate a pierdut si timpul,
                 si increderea ca stie cum merge ecranul. */
              <p className="field__hint">{t('Images or PDF, up to 10 MB each, at most 5.')}</p>
            )}
          </div>

          {replyError ? (
            <p className="field__hint field__hint--error" role="alert">{replyError}</p>
          ) : (
            /* Se spune unde ajunge, fiindcă altfel caseta arată ca o notiţă
               personală. Ajunge la om, şi omul poate răspunde. */
            <p className="field__hint">{t('Goes to whoever has the ticket. They can answer here.')}</p>
          )}

          <button
            type="submit"
            className="btn btn--primary"
            /* Dezactivat şi pe gol, nu doar pe trimis: un buton care acceptă
               apăsarea şi apoi arată o eroare de validare pentru o casetă
               vizibil goală nu spune nimic ce nu se vedea deja. */
            disabled={sending || (reply.trim() === '' && files.length === 0)}
          >
            {sending ? t('Sending…') : t('Send')}
          </button>
        </form>
      </section>
        </aside>
      </div>
      {preview !== null ? (
        <FilePreview ticketId={id} file={preview} onClose={() => setPreview(null)} />
      ) : null}
    </>
  );
}

